import Person2Icon from "@mui/icons-material/Person2";
import { HOST_DOMAIN } from "../../utils/domain";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { SxProps, Theme, useTheme } from "@mui/material";
import { ButtonName, InlineSvgIcon, parseThemeUrl } from "@brandclub/common-ui";
import { useUserProfile } from "@/hooks/useUserProfile";
import { ShopAppButton } from "@/components/button/ShopAppButton";

const BCProfileButton = ({ mobile }: { mobile?: boolean }) => {
  const bcRouting = useBCRouting();
  // isFetched stays true when the profile is asked again. isLoading goes true again
  // for a signed-out shopper, and the button blinks.
  const { data: userProfile, isFetched: profileAnswered } = useUserProfile();

  const handleProfileClick = () => {
    if (userProfile) {
      bcRouting.navigate(HOST_DOMAIN + "/me", "_blank");
    } else {
      bcRouting.location.href =
        HOST_DOMAIN + "/signIn?ssoRedirectUrl=" + encodeURIComponent(bcRouting.location.href);
    }
  };

  const theme = useTheme();
  const content = mobile
    ? theme.content?.profileButton?.signedOutMobile
    : theme.content?.profileButton?.signedOutDesktop;
  const customImage = parseThemeUrl(
    content?.customIconStartSrc ?? content?.customIconEndSrc
  );
  const iconLabel = content?.iconLabel;

  const profileButtonStyleOverride =
    theme.componentCSSOverrides?.[ButtonName.ProfileButton]?.[
      mobile ? "xs" : "md"
    ];
  const iconSize: number | string =
    (profileButtonStyleOverride?.width ?? profileButtonStyleOverride?.height ?? 24) as number | string;

  const textButton = !customImage;

  // Show nothing until the first profile answer, so the button does not appear and then go.
  if (!profileAnswered || !content) {
    return null;
  }

  if (textButton) {
    const text = content?.text;
    return (
      <ShopAppButton
        sx={profileButtonStyleOverride as SxProps<Theme>}
        disableResponsiveStyles
        variant="text"
        color="default"
        onClick={handleProfileClick}
        buttonName={ButtonName.ProfileButton}
      >
        <span>{text}</span>
      </ShopAppButton>
    );
  }

  return (
    <ShopAppButton
      onClick={handleProfileClick}
      variant="icon"
      color="default"
      buttonName={ButtonName.ProfileButton}
      sx={profileButtonStyleOverride as SxProps<Theme>}
      style={{
        display: "flex",
        flexDirection: iconLabel ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      {customImage ? (
        <div style={{ flexShrink: 0, width: iconSize, height: iconSize }}>
          <InlineSvgIcon
            src={customImage}
            alt="profile Icon"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
      ) : (
        <Person2Icon />
      )}
      {iconLabel && (
        <span
          className="bc-icon-label"
          style={{
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 400,
            color: "currentColor",
            whiteSpace: "nowrap",
            marginTop: 7,
            pointerEvents: "none",
          }}
        >
          {iconLabel}
        </span>
      )}
    </ShopAppButton>
  );
};

export default BCProfileButton;
